import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import api, { formatIDR } from "@/lib/api";
import { Button } from "@/components/ui/button";

const PAPER_SIZES = [
  { key: "58", label: "58 mm", mm: 58, px: 220 },
  { key: "80", label: "80 mm", mm: 80, px: 302 },
];

export default function KitchenPrint() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [outlet, setOutlet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paper, setPaper] = useState(() => localStorage.getItem("resto_kitchen_paper") || "80");

  useEffect(() => {
    localStorage.setItem("resto_kitchen_paper", paper);
  }, [paper]);

  useEffect(() => {
    const load = async () => {
      if (!orderId) return;
      setLoading(true);
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        setOrder(data);
      } catch (err) {
        setError(err.response?.data?.detail || "Pesanan tidak ditemukan");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orderId]);

  useEffect(() => {
    if (!order?.outlet_slug) return;
    (async () => {
      try {
        const { data } = await api.get(`/outlets/by-slug/${order.outlet_slug}`);
        setOutlet(data);
      } catch {}
    })();
  }, [order?.outlet_slug]);

  const print = () => window.print();

  const createdAt = order ? new Date(order.created_at).toLocaleString("id-ID", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit"
  }) : "";

  const size = PAPER_SIZES.find((s) => s.key === paper) || PAPER_SIZES[1];

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 text-slate-900" data-testid="kitchen-print-page">
      <style>{`
        @media print {
          @page { size: ${size.mm}mm auto; margin: 0; }
          html, body { background: #fff !important; }
          .no-print { display: none !important; }
          body * { visibility: hidden !important; }
          #kitchen-print-root, #kitchen-print-root * { visibility: visible !important; }
          #kitchen-print-root {
            position: absolute !important; left: 0 !important; top: 0 !important;
            width: ${size.mm}mm !important; max-width: ${size.mm}mm !important;
            margin: 0 !important; padding: 3mm !important;
            border: none !important; box-shadow: none !important; border-radius: 0 !important;
          }
        }
      `}</style>

      <div className="mx-auto w-full max-w-3xl space-y-6">
        <div className="no-print flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">Kitchen Print</h1>
            <p className="text-sm text-muted-foreground">Cetak slip pesanan untuk dapur / printer thermal.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full border border-border bg-white p-1" data-testid="paper-size-switch">
              {PAPER_SIZES.map((s) => (
                <button key={s.key} onClick={() => setPaper(s.key)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors ${paper === s.key ? "primary-action text-primary-foreground font-medium" : "text-muted-foreground hover:bg-accent"}`}
                  data-testid={`paper-${s.key}-btn`}>
                  {s.label}
                </button>
              ))}
            </div>
            <Button variant="outline" onClick={() => navigate(-1)} className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" /> Kembali
            </Button>
            <Button onClick={print} className="rounded-full">
              <Printer className="w-4 h-4 mr-2" /> Cetak
            </Button>
          </div>
        </div>

        {loading && <div className="no-print rounded-xl border border-border bg-white p-6 text-center">Memuat pesanan...</div>}
        {error && <div className="no-print rounded-xl border border-destructive/20 bg-destructive/10 p-6 text-center text-destructive">{error}</div>}

        {order && (
          <div id="kitchen-print-root"
               className="mx-auto bg-white font-mono text-[11px] leading-relaxed text-black"
               style={{ width: size.px, padding: "14px" }}>
            <div className="text-center">
              <p className="font-bold text-[13px] uppercase tracking-wide">{outlet?.name || "Restoran"}</p>
              {outlet?.address && <p className="mt-0.5">{outlet.address}</p>}
              {outlet?.phone && <p>{outlet.phone}</p>}
            </div>
            <div className="my-2 border-t border-dashed border-black" />
            <div className="flex justify-between"><span>No. Pesanan</span><span className="font-bold">#{order.order_number}</span></div>
            <div className="flex justify-between"><span>{(order.order_type === 'room' ? 'Kamar' : 'Meja')}</span><span className="font-bold">{order.table_number}</span></div>
            <div className="flex justify-between"><span>Tanggal</span><span>{createdAt}</span></div>
            {order.customer_name && <div className="flex justify-between"><span>Nama</span><span>{order.customer_name}</span></div>}
            <div className="my-2 border-t border-dashed border-black" />
            {order.items.map((item, index) => (
              <div key={index} className="mb-1.5">
                <div className="flex justify-between gap-2">
                  <span className="font-bold">{item.quantity} × {item.name}</span>
                  <span>{formatIDR(item.price * item.quantity)}</span>
                </div>
                {item.notes && <p className="pl-3">• {item.notes}</p>}
              </div>
            ))}
            <div className="my-2 border-t border-dashed border-black" />
            <div className="flex justify-between"><span>Subtotal</span><span>{formatIDR(order.subtotal)}</span></div>
            {(order.discount_amount || 0) > 0 && (
              <div className="flex justify-between font-bold" data-testid="kitchen-discount-row">
                <span>Diskon{order.discount_type === "percent" && order.discount_value ? ` (${order.discount_value}%)` : ""}</span>
                <span>-{formatIDR(order.discount_amount)}</span>
              </div>
            )}
            {(order.tax_percent > 0 || order.tax_amount > 0) && (
              <div className="flex justify-between"><span>Pajak ({order.tax_percent || 0}%)</span><span>{formatIDR(order.tax_amount || 0)}</span></div>
            )}
            {(order.service_percent > 0 || order.service_amount > 0) && (
              <div className="flex justify-between"><span>Service ({order.service_percent || 0}%)</span><span>{formatIDR(order.service_amount || 0)}</span></div>
            )}
            <div className="mt-1 flex justify-between text-[13px] font-bold"><span>TOTAL</span><span>{formatIDR(order.total)}</span></div>
            {order.notes && (
              <>
                <div className="my-2 border-t border-dashed border-black" />
                <p className="font-bold">Catatan:</p>
                <p>{order.notes}</p>
              </>
            )}
            <div className="my-2 border-t border-dashed border-black" />
            <p className="text-center font-bold">*** SLIP DAPUR ***</p>
            <p className="text-center">Terima kasih</p>
          </div>
        )}
      </div>
    </div>
  );
}
